/** Pitch arithmetic for the score: note names, MIDI numbers, Hz, and the pulse wave's Fourier series. Pure. */

const SEMITONE: Record<string, number> = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };

/** "D4", "F#3", "Bb2" -> MIDI note number (A4 = 69), or null if it is not a note name. */
export function parseNote(name: string): number | null {
  const m = /^([A-G])([#b]?)(-?\d)$/.exec(name);
  if (!m) return null;
  const base = SEMITONE[m[1] ?? ''];
  if (base === undefined) return null;
  const accidental = m[2] === '#' ? 1 : m[2] === 'b' ? -1 : 0;
  return (Number(m[3]) + 1) * 12 + base + accidental;
}

export function midiToHz(midi: number): number {
  return 440 * Math.pow(2, (midi - 69) / 12);
}

/** The lowest and highest notes any pattern may use: below C1 is mud, above C8 is pain. */
export const MIDI_MIN = 24;
export const MIDI_MAX = 108;

/**
 * Desert colour (07 §4): turn natural minor into phrygian by flattening the second degree.
 * `tonic` is the key's pitch class (D = 2); every note a major second above it drops a semitone.
 */
export function toPhrygian(midi: number, tonic: number): number {
  return (((midi - tonic) % 12) + 12) % 12 === 2 ? midi - 1 : midi;
}

/**
 * The "major lift" (07 §4): the parallel major of a natural-minor line - the third, sixth and
 * seventh degrees each rise a semitone. The theme is the same tune, and suddenly the sun is out.
 */
export function toMajor(midi: number, tonic: number): number {
  const degree = (((midi - tonic) % 12) + 12) % 12;
  return degree === 3 || degree === 8 || degree === 10 ? midi + 1 : midi;
}

/**
 * Fourier coefficients of a pulse wave with the given duty cycle, for `createPeriodicWave`:
 * the SID chip's signature voice. Index 0 is DC and stays zero.
 */
export function pulseCoefficients(duty: number, harmonics: number): { real: Float32Array; imag: Float32Array } {
  const real = new Float32Array(harmonics + 1);
  const imag = new Float32Array(harmonics + 1);
  for (let n = 1; n <= harmonics; n++) {
    real[n] = (2 * Math.sin(2 * Math.PI * n * duty)) / (Math.PI * n);
    imag[n] = (2 * (1 - Math.cos(2 * Math.PI * n * duty))) / (Math.PI * n);
  }
  return { real, imag };
}
